import { useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, KeyRound, Trash2, UserPlus, Mail, Phone, Pencil } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { StatesPicker, alertSummary } from "./AlertStatesPicker";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "./ui/alert-dialog";

const EMPTY = { name: "", pin: "", email: "", cell: "", states: [] };
const pinOnly = (v) => v.replace(/\D/g, "").slice(0, 4);

export const AddSignInForm = ({ onAdded }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: k === "pin" ? pinOnly(e.target.value) : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success(`${form.name} can now sign in with PIN ${form.pin}`);
      setForm(EMPTY);
      onAdded();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="rounded-2xl border border-blue-100 bg-blue-50/40 p-4 grid sm:grid-cols-2 lg:grid-cols-6 gap-2" data-testid="add-signin-form">
      <Input placeholder="Full name" required value={form.name} onChange={set("name")} className="bg-white lg:col-span-1" data-testid="add-signin-name" />
      <Input placeholder="4-digit PIN" required inputMode="numeric" pattern="\d{4}" value={form.pin} onChange={set("pin")} className="bg-white font-mono" data-testid="add-signin-pin" />
      <Input placeholder="Email" type="email" required value={form.email} onChange={set("email")} className="bg-white" data-testid="add-signin-email" />
      <Input placeholder="Cell phone" value={form.cell} onChange={set("cell")} className="bg-white" data-testid="add-signin-cell" />
      <StatesPicker key={form.states.join()} value={form.states} testid="add-signin-states-popover" title="States they can see" emptyNote="None selected = every state"
        note="Leads, map, counts, sharing and scans are limited to these states."
        onSave={async (states) => setForm((f) => ({ ...f, states }))}
        trigger={<button type="button" className="h-9 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-700 truncate" data-testid="add-signin-states">{alertSummary(form.states)}</button>} />
      <Button type="submit" disabled={busy || form.pin.length !== 4} className="rounded-full bg-blue-700 hover:bg-blue-800" data-testid="add-signin-submit">
        <UserPlus className="h-4 w-4 mr-1.5" />Add person
      </Button>
    </form>
  );
};

export const PinControls = ({ u, reload }) => {
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(false);
  const [pin, setPin] = useState("");

  const save = async () => {
    try {
      await api.put(`/users/${u.id}/pin`, { pin });
      toast.success(`${u.name}'s PIN is now ${pin}`);
      setEditing(false); setPin(""); reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (editing) return (
    <div className="flex items-center gap-1.5">
      <Input autoFocus inputMode="numeric" placeholder="New PIN" value={pin} onChange={(e) => setPin(pinOnly(e.target.value))} className="h-9 w-24 font-mono" data-testid={`pin-input-${u.id}`} />
      <Button size="sm" disabled={pin.length !== 4} onClick={save} className="rounded-full bg-blue-700 hover:bg-blue-800" data-testid={`pin-save-${u.id}`}>Save</Button>
      <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setPin(""); }} data-testid={`pin-cancel-${u.id}`}>Cancel</Button>
    </div>
  );
  return (
    <div className="flex items-center gap-2">
      <button onClick={() => setShow((s) => !s)} className="h-9 px-3 rounded-full bg-slate-100 text-sm font-mono text-slate-800 flex items-center gap-1.5 hover:bg-slate-200 transition-colors" data-testid={`pin-toggle-${u.id}`}>
        {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
        <span data-testid={`pin-value-${u.id}`}>{show ? (u.pin || "----") : "••••"}</span>
      </button>
      <button onClick={() => setEditing(true)} className="h-9 px-4 rounded-full border border-slate-200 text-sm font-medium text-blue-700 hover:border-blue-400 transition-colors flex items-center gap-1.5" data-testid={`pin-change-${u.id}`}>
        <KeyRound className="h-4 w-4" />Change PIN
      </button>
    </div>
  );
};

export const ContactControls = ({ u, reload }) => {
  const c = u.contact;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ email: "", cell: "" });
  const start = () => { setForm({ email: c?.email || "", cell: c?.cell || "" }); setEditing(true); };
  const save = async () => {
    try {
      await api.put(`/users/${u.id}/contact`, form);
      toast.success(`${u.name}'s contact saved`);
      setEditing(false); reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (editing) return (
    <div className="flex flex-wrap items-center gap-1.5 w-full">
      <Input autoFocus type="email" placeholder="Email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="h-9 flex-1 min-w-[180px]" data-testid={`contact-email-input-${u.id}`} />
      <Input placeholder="Cell phone" value={form.cell} onChange={(e) => setForm((f) => ({ ...f, cell: e.target.value }))} className="h-9 w-36" data-testid={`contact-cell-input-${u.id}`} />
      <Button size="sm" disabled={!form.email.trim()} onClick={save} className="rounded-full bg-blue-700 hover:bg-blue-800" data-testid={`contact-save-${u.id}`}>Save</Button>
      <Button size="sm" variant="ghost" onClick={() => setEditing(false)} data-testid={`contact-cancel-${u.id}`}>Cancel</Button>
    </div>
  );
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm w-full" data-testid={`contact-${u.id}`}>
      {c ? <>
        <span className="flex items-center gap-1.5 text-slate-700"><Mail className="h-3.5 w-3.5 text-slate-400" />{c.email}</span>
        <span className="flex items-center gap-1.5 text-slate-700"><Phone className="h-3.5 w-3.5 text-slate-400" />{c.cell || "No cell"}</span>
      </> : <span className="text-amber-700 text-xs" data-testid={`contact-missing-${u.id}`}>No email yet: they won't get lead emails</span>}
      <button onClick={start} className="text-xs font-semibold text-blue-700 flex items-center gap-1 hover:underline" data-testid={`contact-edit-${u.id}`}>
        <Pencil className="h-3 w-3" />{c ? "Edit" : "Add email"}
      </button>
    </div>
  );
};

export const RemoveSignIn = ({ u, reload }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <Button variant="ghost" size="icon" className="rounded-full text-slate-500 hover:text-red-600" data-testid={`signin-remove-${u.id}`}><Trash2 className="h-4 w-4" /></Button>
    </AlertDialogTrigger>
    <AlertDialogContent data-testid="signin-remove-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle>Remove {u.name}'s sign-in?</AlertDialogTitle>
        <AlertDialogDescription>Their PIN stops working right away and they stop getting lead emails. Their lead history stays.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="signin-remove-cancel">Cancel</AlertDialogCancel>
        <AlertDialogAction className="bg-red-600 hover:bg-red-700" data-testid="signin-remove-confirm" onClick={async () => {
          try { await api.delete(`/users/${u.id}`); toast.success(`${u.name} can no longer sign in`); reload(); }
          catch (err) { toast.error(formatApiError(err)); }
        }}>Remove sign-in</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);
